// client/src/pages/seo/QrCodeMenuPrintingNo.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";

const sections = [
  { id: "before", label: "Hva må være klart før trykking?" },
  { id: "materials", label: "Bordskilt, kort, klistremerker eller holdere?" },
  { id: "design", label: "Størrelse, kontrast og lesbarhet" },
  { id: "placement", label: "Hvor bør QR-koden plasseres?" },
  { id: "test", label: "Sjekkliste før du bestiller trykk" },
  { id: "faq", label: "Vanlige spørsmål om QR-kode-trykk" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeMenuPrintingNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title = "Trykk QR-kode til meny: størrelse og plassering | Best Place";
    setMeta(
      "description",
      "Trykk QR-kode til restaurantmenyen: praktiske råd om bordskilt, kort, klistremerker, kontrast, plassering og skannetesting før trykk."
    );
    setCanonical("https://best-place.club/no/qr-kode-meny-utskrift");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          en: "/en/qr-code-menu-printing",
          de: "/de/qr-code-speisekarte-drucken",
          no: "/no/qr-kode-meny-utskrift",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>Trykk QR-kode til restaurantmeny: materiale, størrelse og plassering</span>
          </h1>
          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>
            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Før du bestiller bordskilt, kort eller klistremerker, bør den digitale målsiden være ferdig og QR-koden fungere pålitelig på ekte smarttelefoner.
              </p>
              <p className="seoHero__text">
                Denne siden handler bare om den fysiske bruken av QR-koden: trykkmateriale, lesbarhet, plassering og testing. For selve den digitale QR-menyen, se hovedsiden vår.
              </p>
              <p><Link to="/no/qr-kode-meny-for-restauranter">Gå til QR-kode-menyer for restauranter</Link></p>
            </div>
          </div>
        </div>
      </section>

      <section className="articls">
        <h2>Innhold</h2>
        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button type="button" className="qrArticle__navButton" onClick={() => scrollToSection(item.id)}>
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="before" className="acquaintance">
          <h2>Hva må være klart før trykking?</h2>
          <p>
            Den vanligste feilen er å bestille flotte trykkmaterialer før det er klart hvor QR-koden faktisk leder. Først bør menysiden, mål-URL-en og gjestevisningen fungere. Først da gir det mening å produsere større mengder.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Menyens URL er stabil og offentlig tilgjengelig.</li>
            <li className="text__seo-li">Menyen fungerer på flere smarttelefoner.</li>
            <li className="text__seo-li">QR-koden er testet fra det endelige designet.</li>
            <li className="text__seo-li">Innholdsoppdateringer krever ikke at den trykte koden byttes ut.</li>
          </ul>
        </section>

        <section id="materials" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Bordskilts, cards, stickers or holders?</h2>
            <div className="qrArticle__cardGrid">
              <div>
                <strong>Table tent</strong>
                <p>Lett å se og lett å bytte ut. Praktisk for restauranter med faste bord.</p>
              </div>
              <div>
                <strong>Lite bordkort</strong>
                <p>Rimelig for testing og sesongendringer før du velger et permanent materiale.</p>
              </div>
              <div>
                <strong>Klistremerke</strong>
                <p>Sparer plass, men er mindre fleksibelt hvis design eller plassering må endres senere.</p>
              </div>
              <div>
                <strong>Treholder eller 3D-printet holder</strong>
                <p>Kan se eksklusivt ut, men bør først produseres etter en vellykket test i praksis.</p>
              </div>
            </div>
            <p>
              For temaet bordskilt, se også siden vår{" "}
              <Link to="/no/restaurant-qr-kode-bordskilt">QR-kode-bordskilt for restaurant</Link>.
            </p>
          </div>
        </section>

        <section id="design" className="acquaintance">
          <h2>Størrelse, kontrast og lesbarhet</h2>
          <p>
            QR-koder bør ikke designes i den teoretiske minimumsstørrelsen. I en restaurant påvirker avstand, lys, refleksjoner, kamera og synsvinkel skanningen. I praksis er en litt større kode med høy kontrast vanligvis mer pålitelig enn en svært liten, dekorativ kode.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">høy kontrast mellom kode og bakgrunn</li>
            <li className="text__seo-li">nok fri plass rundt koden</li>
            <li className="text__seo-li">ingen sterke refleksjoner over koden</li>
            <li className="text__seo-li">tekst ved siden av som forklarer hva som åpnes</li>
            <li className="text__seo-li">skannetest direkte fra den endelige trykkfilen</li>
          </ul>
          <p>
            En tydelig tekst som «Skann menyen» eller «Åpne menyen uten app» fjerner usikkerhet om hva som skjer etter skanningen.
          </p>
        </section>

        <section id="placement" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvor bør QR-koden plasseres?</h2>
            <p>
              Den beste plasseringen er der gjesten faktisk trenger menyen. På et restaurantbord betyr det vanligvis godt synlig, uten at gjesten må lete aktivt etter den.
            </p>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">bordskilt eller bordkort</li>
              <li className="text__seo-li">disk eller selvbetjeningsområde</li>
              <li className="text__seo-li">vindu eller inngang for informasjon før gjesten går inn</li>
              <li className="text__seo-li">flyere og takeaway-emballasje for senere tilgang</li>
            </ul>
            <p>
              Unngå plasseringer som stadig dekkes av glass, tallerkener eller dekorasjon.
            </p>
          </div>
        </section>

        <section id="test" className="acquaintance">
          <h2>Printing checklist before ordering</h2>
          <ol className="qrArticle__block-li">
            <li className="text__seo-li">Åpne og kontroller den endelige meny-URL-en.</li>
            <li className="text__seo-li">Skann QR-koden fra den endelige trykkfilen.</li>
            <li className="text__seo-li">Test med iPhone og Android.</li>
            <li className="text__seo-li">Test fra en vanlig sitteavstand.</li>
            <li className="text__seo-li">Test under normal restaurantbelysning.</li>
            <li className="text__seo-li">Produser et lite testopplag først.</li>
          </ol>
          <p>
            Først når disse punktene fungerer, bør du bestille større mengder eller produsere mer avanserte holdere.
          </p>
        </section>

        <section id="faq" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Vanlige spørsmål om QR-kode-trykk</h2>
            <div className="seoFaq">
              <details>
                <summary>Må jeg trykke QR-koden på nytt hver gang menyen endres?</summary>
                <p>Nei, så lenge koden peker til en stabil meny-URL og bare innholdet bak den endres.</p>
              </details>
              <details>
                <summary>Hva er best: klistremerke eller bordskilt?</summary>
                <p>Bordskilt og utskiftbare kort er mer fleksible. Klistremerker er mer permanente, men vanskeligere å endre senere.</p>
              </details>
              <details>
                <summary>Kan jeg bruke en 3D-printet holder?</summary>
                <p>Ja. En holder for et utskiftbart kort med en tydelig trykt QR-kode er en praktisk løsning.</p>
              </details>
              <details>
                <summary>Bør jeg fjerne de trykte menyene med en gang?</summary>
                <p>Ikke nødvendigvis. Mange restauranter bruker digitale og trykte menyer parallelt.</p>
              </details>
            </div>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Relaterte temaer</h2>
          <div className="seoCompareSmall">
            <p><Link to="/no/qr-kode-meny-for-restauranter">QR-kode-meny for restauranter</Link></p>
            <p><Link to="/no/restaurant-qr-kode-bordskilt">QR-kode-bordskilt</Link></p>
            <p><Link to="/no/qr-kode-meny-uten-app">QR-meny uten app</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
